import React, { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { ISanityImage } from '@rbi-ctg/menu';
import { Picture } from 'components/picture';
import { UnstyledButton } from 'components/unstyled-button';
import { MenuObjectTypes } from 'enums/menu';
import { brandFont } from 'styles/brand-font';
import { primitive } from 'styles/constants/primitives';
import { NutritionExplorerMenuObject } from 'types/nutrition-explorer-menu';

import { DropdownArrow, defaultOrientation, defaultSize } from './dropdown-arrow';
import { ProductCard } from './product-card';
import { IProduct } from './types';

const Container = styled.section`
  width: 100%;
  & + & {
    border-block-start: 1px solid ${p => p.theme.token('border-color-default')};
  }
`;

const Header = styled(UnstyledButton)`
  display: flex;
  flex-direction: row;
  align-items: center;

  margin: 0;
  padding: ${primitive.$spacing3} 0;
  width: 100%;

  color: ${p => p.theme.token('text-default')};
  pointer-events: inherit;

  &:disabled {
    color: ${p => p.theme.token('text-disabled')};
    pointer-events: none;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: start;
  padding-inline-start: ${primitive.$spacing2};
  overflow: hidden;

  ${Styles.desktop} {
    padding-inline-start: ${primitive.$spacing3};
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
`;

const sharedTitleCss = css`
  margin: 0;

  font: ${brandFont.headerTwo};
  letter-spacing: 1px;
  text-transform: ${Styles.textTransform.headlines};
`;

const TitleText = styled.h2`
  ${sharedTitleCss};

  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const CountText = styled.h2`
  ${sharedTitleCss};

  margin-inline-start: ${primitive.$spacing2};
  flex-shrink: 1;
`;

const Title: React.FC<{ title: string; count: number }> = ({ title, count }) => (
  <TitleContainer>
    <TitleText>{title}</TitleText>
    <CountText>{`(${count})`}</CountText>
  </TitleContainer>
);

const Caption = styled.p`
  margin: 0;

  font: ${brandFont.copyTwo};
`;

const PreviewPicture = styled(Picture)<{ disabled: boolean }>`
  flex-shrink: 0;
  margin-inline-start: ${primitive.$spacing2};

  width: 40px;
  height: 48px;

  ${Styles.desktop} {
    width: 64px;
    height: 64px;
    margin-inline-start: ${primitive.$spacing3};
  }

  opacity: ${p => (p.disabled ? '0.7' : '1')};
`;

const ProductCardSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${primitive.$spacing6} ${primitive.$spacing3};
  position: relative;
  width: 100%;
  margin: 0 auto;
  padding: ${primitive.$spacing4} 0;

  ${Styles.desktop} {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-gap: ${primitive.$spacing7} ${primitive.$spacing4};
  }

  ${Styles.mobileTiny} {
    grid-template-columns: 1fr;
  }
`;

interface IAccordion {
  parentMenuObject: NutritionExplorerMenuObject;
  products: IProduct[];
  startsOpen: boolean;
  productBackgroundImage?: ISanityImage;
}

export const Accordion: React.FC<IAccordion> = ({
  parentMenuObject,
  products,
  startsOpen,
  productBackgroundImage,
}) => {
  const [open, setOpen] = useState(startsOpen);
  const { formatMessage } = useIntl();

  const disabled = products.length === 0;

  const onClickHeader = useCallback(() => {
    if (!disabled) {
      setOpen(isOpen => !isOpen);
    }
  }, [disabled]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  const image =
    parentMenuObject._type === MenuObjectTypes.SECTION
      ? parentMenuObject.carouselImage
      : parentMenuObject.image;

  return (
    <Container>
      <Header onClick={onClickHeader} disabled={disabled} aria-expanded={open}>
        <DropdownArrow
          open={open}
          disabled={disabled}
          size={defaultSize}
          orientation={defaultOrientation}
        />
        <PreviewPicture image={image} alt="" objectFitContain disabled={disabled} />
        <TextContainer>
          <Title title={parentMenuObject.name?.locale} count={products.length} />
          {disabled && <Caption>{formatMessage({ id: 'noItemsFitCriteria' })}</Caption>}
        </TextContainer>
      </Header>
      {open ? (
        <ProductCardSection>
          {products.map(product => (
            <ProductCard
              product={product}
              key={product._id}
              backgroundImage={productBackgroundImage}
            />
          ))}
        </ProductCardSection>
      ) : null}
    </Container>
  );
};
